import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Meta, Title } from '@angular/platform-browser';
import {
  ActivatedRoute,
  NavigationStart,
  Router,
  RouterModule,
} from '@angular/router';
import {
  catchError,
  filter,
  forkJoin,
  Observable,
  of,
  Subject,
  Subscription,
  take,
  takeUntil,
  timeout,
} from 'rxjs';

import { EventLandingPage } from '../../core/models/event-landing-page.model';
import { AuthService } from '../../core/services/auth.service';
import { ProfileService } from '../../core/services/profile.service';
import {
  EventStartAttemptService,
  EVENT_START_RETRY_WARNING,
} from '../../core/services/event-start-attempt.service';
import { EventRsvpIntentService } from '../../core/services/event-rsvp-intent.service';
import {
  EventCta,
  EventViewerState,
  isTerminalRsvpReason,
  rsvpReasonMessage,
  selectEventCta,
} from '../../core/util/event-rsvp-state.util';
import {
  EventLandingBannerComponent,
  EventLandingCountdown,
} from '../../shared/event-landing-banner/event-landing-banner.component';
import { ShareModalComponent } from '../../search/share-modal/share-modal/share-modal.component';
import { CalendarPickerComponent } from '../../shared/calendar-picker/calendar-picker.component';
import { EventsDTO } from '../../core/models/events.modal';
import {
  getWebinarStatusError,
  getHostWebinarStatusError,
  hasCompleteHostWebinarRoomDetails,
} from '../../core/util/webinar.util';
import { getAppRsvpUrl } from '../../core/util/event-app-handoff.util';
import { eventDetailsRoute } from '../../core/util/event-route.util';

@Component({
  selector: 'app-event-landing-page',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    EventLandingBannerComponent,
    ShareModalComponent,
    CalendarPickerComponent,
  ],
  templateUrl: './event-landing-page.component.html',
  styleUrl: './event-landing-page.component.scss',
})
export class EventLandingPageComponent implements OnInit, OnDestroy {
  @ViewChild('shareModal') shareModal?: ShareModalComponent;

  page: EventLandingPage | null = null;
  countdown: EventLandingCountdown | null = null;
  isLoading = true;
  isUnavailable = false;
  loadError = false;
  isRefreshing = false;
  hasScheduledStartArrived = false;
  private checkedWaiting = false;
  viewerState: EventViewerState = {};
  isViewerStateLoading = false;
  viewerStateError = false;
  isRsvpWorking = false;
  rsvpStatusMessage = '';
  appRsvpUrl: string | null = null;

  private routeSubscription?: Subscription;
  private navigationSubscription?: Subscription;
  private countdownTimer?: ReturnType<typeof setInterval>;
  private readonly readTimeoutMs = 10_000;
  private readonly requestsCancelled = new Subject<void>();
  private requestedEventId?: number;
  private requestedSlug: string | null = null;
  private loadVersion = 0;
  private canonicalLink?: HTMLLinkElement;
  private canonicalRedirectPage?: EventLandingPage;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private profileService: ProfileService,
    private authService: AuthService,
    private title: Title,
    private meta: Meta,
    @Inject(DOCUMENT) private document: Document,
    private rsvpIntentService: EventRsvpIntentService = new EventRsvpIntentService(),
    private startAttempts: EventStartAttemptService = new EventStartAttemptService()
  ) {}

  ngOnInit(): void {
    const routerEvents = (this.router as any).events;
    if (routerEvents) {
      this.navigationSubscription = routerEvents
        .pipe(
          filter(
            (event: unknown): event is NavigationStart =>
              event instanceof NavigationStart
          )
        )
        .subscribe((event: NavigationStart) =>
          this.restoreDefaultMetadata(event.url)
        );
    }
    this.routeSubscription = this.route.paramMap.subscribe((params) => {
      const eventId = Number(params.get('eventId'));
      const suppliedSlug = params.get('slug');
      if (!Number.isSafeInteger(eventId) || eventId <= 0) {
        this.loadVersion += 1;
        this.requestsCancelled.next();
        this.showUnavailable();
        return;
      }
      this.loadLandingPage(eventId, suppliedSlug);
    });
  }

  ngOnDestroy(): void {
    this.appRsvpUrl = null;
    this.loadVersion += 1;
    this.requestsCancelled.next();
    this.requestsCancelled.complete();
    this.routeSubscription?.unsubscribe();
    this.navigationSubscription?.unsubscribe();
    this.stopCountdown();
    this.restoreDefaultMetadata();
  }

  get ctaMode(): 'guest' | 'authenticated' {
    return this.authService.getLogflag() ? 'authenticated' : 'guest';
  }

  get canonicalPath(): string {
    if (!this.page) {
      return '/events';
    }
    return `/events/${this.page.eventId}/${encodeURIComponent(this.page.slug)}`;
  }

  get displayPage(): EventLandingPage | null {
    return this.page;
  }

  get canonicalUrl(): string {
    return `${this.document.location.origin}${this.canonicalPath}`;
  }

  get eventCta(): EventCta {
    return selectEventCta(
      this.page?.status,
      this.viewerState,
      this.authService.getLogflag()
    );
  }

  get isHost(): boolean {
    return Boolean(
      this.page && this.authService.getLogflag() && this.viewerState.isHost
    );
  }

  get primaryCtaLabel(): string {
    if (this.isHost && this.hasUnconfirmedStart)
      return this.hasStartInFlight ? 'Start in progress…' : 'Try Start again';
    if (this.isHost)
      return this.page?.status === 'LIVE' ? 'Rejoin Event' : 'Start Event';
    if (this.viewerStateError) return 'Retry access check';
    if (this.isViewerStateLoading) return 'Checking access…';
    if (
      this.eventCta === 'SIGN_IN_TO_WATCH' &&
      this.viewerState.capacityReached
    ) {
      return 'Already admitted? Sign in to watch';
    }
    const labels: Record<EventCta, string> = {
      RSVP: 'RSVP',
      GOING: 'RSVP’d',
      WATCH: 'Watch live',
      JOIN: 'Join live',
      SIGN_IN_TO_RSVP: 'Sign in to RSVP',
      SIGN_IN_TO_WATCH: 'Sign in to watch',
      ENDED: 'Event ended',
      UNAVAILABLE: 'Unavailable',
      FULL: 'Event full',
      REQUIREMENT: 'View requirement',
    };
    return labels[this.eventCta];
  }

  get showCancelRsvp(): boolean {
    return !this.viewerStateError && !this.isHost && this.eventCta === 'GOING';
  }

  get showJoinNotice(): boolean {
    return !this.isHost && this.eventCta === 'JOIN';
  }

  get showSecondaryRsvp(): boolean {
    return (
      !this.viewerStateError &&
      !this.isHost &&
      (this.eventCta === 'JOIN' || this.eventCta === 'WATCH') &&
      this.authService.getLogflag() &&
      (Boolean(this.viewerState.isRsvp) || Boolean(this.viewerState.canRsvp))
    );
  }

  get canAddEventToCalendar(): boolean {
    return Boolean(
      this.page &&
        this.authService.getLogflag() &&
        this.page.status !== 'ENDED' &&
        this.page.status !== 'CANCELLED' &&
        (this.viewerState.isRsvp || this.isHost)
    );
  }

  get secondaryRsvpLabel(): string {
    return this.viewerState.isRsvp ? 'Cancel RSVP' : 'RSVP';
  }

  get watchNotice(): string {
    return this.eventCta === 'JOIN'
      ? 'Your public name will be visible to other participants.'
      : '';
  }

  get isBusy(): boolean {
    return this.isRsvpWorking || this.isRefreshing || this.isViewerStateLoading;
  }

  get showStatusCheck(): boolean {
    return this.page?.status === 'UPCOMING' || this.page?.status === 'LIVE';
  }

  get isWaitingForHost(): boolean {
    return (
      this.page?.status === 'UPCOMING' &&
      this.hasScheduledStartArrived &&
      !this.isHost
    );
  }

  get waitingMessage(): string {
    if (!this.isWaitingForHost) return '';
    return this.checkedWaiting
      ? "The host hasn't started yet. Check again to join."
      : 'The scheduled start time has arrived. Check event status to see whether the host has started.';
  }

  get hasUnconfirmedStart(): boolean {
    return Boolean(
      this.page &&
        this.page.status !== 'LIVE' &&
        this.startAttempts.isPending(this.page.eventId)
    );
  }

  get hasStartInFlight(): boolean {
    return Boolean(
      this.page && this.startAttempts.isInFlight(this.page.eventId)
    );
  }

  onPrimaryAction(): void {
    if (!this.page || this.isBusy || this.appRsvpUrl) return;
    if (this.page.status === 'ENDED' || this.page.status === 'CANCELLED')
      return;
    if (this.isHost) {
      this.onHostPrimaryAction();
      return;
    }
    if (this.viewerStateError) {
      this.checkEventStatus();
      return;
    }
    const intent = this.eventCta;
    if (intent === 'SIGN_IN_TO_RSVP' || intent === 'SIGN_IN_TO_WATCH') {
      this.refreshEventState(() => this.continueGuestAction());
    } else if (intent === 'JOIN' || intent === 'WATCH') {
      this.refreshEventState(() => {
        if (this.eventCta === intent) this.watchEvent();
      });
    } else if (intent === 'RSVP') {
      this.submitRsvp();
    } else if (intent === 'REQUIREMENT') {
      this.rsvpStatusMessage = rsvpReasonMessage(
        this.viewerState.watchReason || this.viewerState.rsvpReason
      );
    }
  }

  private continueGuestAction(): void {
    if (!this.page || this.authService.getLogflag()) return;
    if (this.eventCta === 'SIGN_IN_TO_RSVP') {
      const appUrl = getAppRsvpUrl(this.page.eventId, this.document);
      if (appUrl) this.appRsvpUrl = appUrl;
      else this.continueRsvpInBrowser();
    } else if (this.eventCta === 'SIGN_IN_TO_WATCH') {
      void this.router.navigate(['/login'], {
        queryParams: { returnUrl: this.canonicalPath },
      });
    } else if (this.eventCta === 'REQUIREMENT') {
      this.rsvpStatusMessage = rsvpReasonMessage(
        this.viewerState.watchReason || this.viewerState.rsvpReason
      );
    }
  }

  checkEventStatus(): void {
    if (this.isBusy || !this.showStatusCheck) return;
    this.refreshEventState();
  }

  retryLoad(): void {
    if (this.requestedEventId && !this.isLoading) {
      this.loadLandingPage(this.requestedEventId, this.requestedSlug);
    }
  }

  private read<T>(request: Observable<T>): Observable<T> {
    return request.pipe(
      timeout(this.readTimeoutMs),
      take(1),
      takeUntil(this.requestsCancelled)
    );
  }

  private isCurrent(eventId: number, version: number): boolean {
    return version === this.loadVersion && this.page?.eventId === eventId;
  }

  openRsvpInApp(): void {
    if (!this.appRsvpUrl) return;
    try {
      this.document.defaultView?.location.assign(this.appRsvpUrl);
    } catch {
      this.rsvpStatusMessage =
        'Could not open CitizeX. Continue in your browser instead.';
    }
  }

  continueRsvpInBrowser(): void {
    if (!this.page) return;
    this.appRsvpUrl = null;
    const eventPath = this.canonicalPath;
    this.rsvpIntentService.clear();
    void this.router.navigate(['/login'], {
      queryParams: { returnUrl: eventPath },
    });
  }

  onCancelRsvp(): void {
    if (
      !this.page ||
      this.isHost ||
      this.isBusy ||
      !this.authService.getLogflag()
    )
      return;
    if (!window.confirm('Cancel your RSVP for this event?')) return;
    this.mutateRsvp(true);
  }

  onSecondaryRsvp(): void {
    if (this.isHost || this.isBusy || this.viewerStateError) return;
    // Capture the labelled operation before any response can change membership.
    if (this.viewerState.isRsvp) this.onCancelRsvp();
    else this.submitRsvp();
  }

  openShareModal(): void {
    this.shareModal?.openModal({ currentUrl: this.canonicalUrl });
  }

  private onHostPrimaryAction(): void {
    if (!this.page) return;
    if (!this.authService.getUser()?.verified) {
      this.rsvpStatusMessage =
        'Verify your account before starting or rejoining this event.';
      return;
    }
    const eventId = this.page.eventId;
    const version = this.loadVersion;
    const intendedStart = this.page.status !== 'LIVE';
    if (this.startAttempts.isInFlight(eventId) && intendedStart) {
      this.rsvpStatusMessage =
        'Start is still in progress. Check event status before trying again.';
      return;
    }
    this.isRsvpWorking = true;
    this.rsvpStatusMessage = '';
    this.read(
      this.profileService.fetchVirtualEventById(
        new HttpParams().set('eventId', String(eventId))
      )
    ).subscribe({
      next: (response: any) => {
        if (!this.isCurrent(eventId, version)) return;
        if (response?.success !== 0 || response?.data?.id !== eventId) {
          this.isRsvpWorking = false;
          this.rsvpStatusMessage = getWebinarStatusError(
            response,
            'Unable to load this event. Please try again.'
          );
          return;
        }
        const event = EventsDTO.fromResponse(response.data);
        const status = event.eventStatus?.trim().toLowerCase();
        if (
          [
            'closed',
            'ended',
            'end',
            'stop',
            'stopped',
            'cancelled',
            'canceled',
            'unpublished',
          ].includes(status)
        ) {
          this.isRsvpWorking = false;
          this.rsvpStatusMessage = 'This event is no longer available.';
          this.reconcileLandingPage();
          return;
        }
        const live = ['live', 'start', 'started'].includes(status);
        if (!live && !intendedStart) {
          this.isRsvpWorking = false;
          this.rsvpStatusMessage =
            'This event is not live. Check event status before starting it.';
          this.reconcileLandingPage();
          return;
        }
        if (!hasCompleteHostWebinarRoomDetails(event)) {
          this.isRsvpWorking = false;
          this.rsvpStatusMessage =
            'This event is missing webinar access details. Open event details to repair it.';
          return;
        }
        const openHostRoom = () => {
          if (!this.isCurrent(eventId, version)) return;
          this.startAttempts.observeLive(eventId);
          this.isRsvpWorking = false;
          void this.router.navigate(['/profile/events/', event.webinarId], {
            queryParams: { isHost: true, openChatRoom: true },
          });
        };
        if (live) {
          openHostRoom();
          return;
        }
        const retry = this.startAttempts.isPending(eventId);
        if (retry && !window.confirm(EVENT_START_RETRY_WARNING)) {
          this.isRsvpWorking = false;
          return;
        }
        if (!this.startAttempts.begin(eventId, retry)) {
          this.isRsvpWorking = false;
          return;
        }
        // A write has no read timeout. Its server-side effects survive disconnection.
        this.profileService
          .updateWebinarStatus(
            new HttpParams()
              .set('eventId', String(eventId))
              .set('webinarStatus', 'START')
          )
          .pipe(take(1))
          .subscribe({
            next: (result: any) => {
              // Completing a write settles the shared guard even after navigation.
              this.startAttempts.settle(eventId);
              if (!this.isCurrent(eventId, version)) return;
              if (result?.success === 0) {
                openHostRoom();
              } else {
                this.handleStartFailure(eventId, result);
              }
            },
            error: (error: any) => {
              this.startAttempts.fail(eventId, error);
              if (this.isCurrent(eventId, version))
                this.handleStartFailure(eventId, error);
            },
          });
      },
      error: (error: any) => {
        if (!this.isCurrent(eventId, version)) return;
        this.isRsvpWorking = false;
        this.rsvpStatusMessage = getWebinarStatusError(
          error,
          'Unable to load this event. Please try again.'
        );
      },
    });
  }

  private handleStartFailure(eventId: number, error: any): void {
    this.isRsvpWorking = false;
    this.rsvpStatusMessage = this.startAttempts.isPending(eventId)
      ? 'The previous Start could not be confirmed. Check event status, or choose Try Start again to check and retry with confirmation.'
      : getHostWebinarStatusError(error);
    this.reconcileLandingPage();
  }

  private loadLandingPage(eventId: number, suppliedSlug: string | null): void {
    const requestVersion = ++this.loadVersion;
    this.requestsCancelled.next();
    this.requestedEventId = eventId;
    this.requestedSlug = suppliedSlug;
    this.isRefreshing = false;
    this.isRsvpWorking = false;
    this.loadError = false;
    this.checkedWaiting = false;
    this.isLoading = true;
    this.isViewerStateLoading = false;
    this.viewerStateError = false;
    this.rsvpStatusMessage = '';
    this.isUnavailable = false;
    this.page = null;
    this.countdown = null;
    this.stopCountdown();

    const redirectedPage = this.canonicalRedirectPage;
    if (
      redirectedPage &&
      redirectedPage.eventId === eventId &&
      redirectedPage.slug === suppliedSlug
    ) {
      this.canonicalRedirectPage = undefined;
      this.loadInitialState(redirectedPage);
      return;
    }
    this.canonicalRedirectPage = undefined;

    this.read(
      this.profileService.fetchPublicEventLandingPage(eventId)
    ).subscribe({
      next: (response: any) => {
        if (requestVersion !== this.loadVersion) return;
        if (response?.success === 0 && !response?.data) {
          this.redirectToEventDetails(eventId, requestVersion);
          return;
        }
        if (response?.success !== 0 || !response?.data) {
          this.showLoadError();
          return;
        }

        const page = response.data as EventLandingPage;
        if (page.eventId !== eventId || !page.slug || !page.hostTag) {
          this.showUnavailable();
          return;
        }

        const canonicalPath = `/events/${page.eventId}/${encodeURIComponent(
          page.slug
        )}`;
        if (suppliedSlug !== page.slug) {
          this.canonicalRedirectPage = page;
          void this.router
            .navigateByUrl(canonicalPath, { replaceUrl: true })
            .then((navigated) => {
              // Router activation normally emits the canonical parameter map
              // before this promise resolves. The fallback covers a cancelled
              // or mocked navigation without issuing a second public request.
              if (navigated && requestVersion === this.loadVersion) {
                this.canonicalRedirectPage = undefined;
                this.loadInitialState(page);
              }
            });
          return;
        }

        this.loadInitialState(page);
      },
      error: () => {
        if (requestVersion === this.loadVersion) {
          this.showLoadError();
        }
      },
    });
  }

  private redirectToEventDetails(
    eventId: number,
    requestVersion: number
  ): void {
    this.read(
      this.profileService.fetchVirtualEventByIdWithoutGlobalLoader(
        new HttpParams().set('eventId', String(eventId))
      )
    ).subscribe({
      next: (response: any) => {
        if (requestVersion !== this.loadVersion) return;
        const route =
          response?.success === 0 && response?.data?.id === eventId
            ? eventDetailsRoute(response.data)
            : null;
        if (route) {
          void this.router.navigate(route, { replaceUrl: true });
        } else {
          this.showUnavailable();
        }
      },
      error: () => {
        if (requestVersion === this.loadVersion) this.showLoadError();
      },
    });
  }

  private loadInitialState(page: EventLandingPage): void {
    this.setPage(page);
    this.applyViewerState(page.viewingState || {});
    this.viewerStateError = false;
    this.refreshViewerState();
  }

  private setPage(page: EventLandingPage): void {
    this.stopCountdown();
    this.page = page;
    this.isLoading = false;
    this.loadError = false;
    this.isUnavailable = false;
    if (!this.authService.getLogflag())
      this.applyViewerState(page.viewingState || {});
    if (page.status === 'LIVE') this.startAttempts.observeLive(page.eventId);
    this.updateCountdown();
    this.applyLandingMetadata(page);
    if (this.countdown)
      this.countdownTimer = setInterval(() => this.updateCountdown(), 1000);
  }

  private refreshViewerState(): void {
    if (
      !this.page ||
      !this.authService.getLogflag() ||
      typeof this.profileService.fetchVirtualEventWatchState !== 'function'
    )
      return;
    const eventId = this.page.eventId;
    const requestVersion = this.loadVersion;
    this.isViewerStateLoading = true;
    this.read(
      this.profileService.fetchVirtualEventWatchState(eventId)
    ).subscribe({
      next: (response: any) => {
        if (
          requestVersion !== this.loadVersion ||
          this.page?.eventId !== eventId
        )
          return;
        if (response?.success !== 0 || !response.data) {
          this.markViewerStateUnavailable();
          return;
        }
        this.applyViewerState(response.data);
        this.isViewerStateLoading = false;
        this.viewerStateError = false;
        this.rsvpStatusMessage = '';
        // A returned guest should see the event and use its RSVP button.
        // Discard an intent left by older versions of this handoff.
        if (
          this.rsvpIntentService.matchesFor(
            this.page.eventId,
            this.canonicalPath
          )
        ) {
          this.rsvpIntentService.clear();
        }
      },
      error: () => {
        if (
          requestVersion !== this.loadVersion ||
          this.page?.eventId !== eventId
        )
          return;
        this.markViewerStateUnavailable();
      },
    });
  }

  retryViewerState(): void {
    this.checkEventStatus();
  }

  private invalidateViewerAccess(): void {
    this.isViewerStateLoading = false;
    this.viewerStateError = true;
    this.viewerState = {
      ...this.viewerState,
      canWatch: false,
      canRsvp: false,
      watchMode: null,
    };
  }

  private markViewerStateUnavailable(): void {
    this.isViewerStateLoading = false;
    this.viewerStateError = true;
    this.rsvpStatusMessage =
      'Unable to verify event access. Select Retry access check to try again.';
    this.applyViewerState({
      canWatch: false,
      canRsvp: false,
      watchReason: 'EVENT_UNAVAILABLE',
      capacityReached: this.page?.viewingState?.capacityReached,
    });
  }

  private applyViewerState(data: EventViewerState): void {
    this.viewerState = {
      isRsvp: Boolean(data?.isRsvp),
      isHost: data?.isHost,
      canRsvp: Boolean(data?.canRsvp),
      rsvpReason: data?.rsvpReason || null,
      canWatch: Boolean(data?.canWatch),
      watchMode: data?.watchMode || null,
      watchReason: data?.watchReason || null,
      chatReason: data?.chatReason || null,
      rsvpCount: data?.rsvpCount,
      capacityReached: Boolean(data?.capacityReached),
      alreadyAdmitted: Boolean(data?.alreadyAdmitted),
    };
  }

  private submitRsvp(): void {
    if (
      !this.page ||
      this.isHost ||
      this.isBusy ||
      !this.authService.getLogflag()
    )
      return;
    this.mutateRsvp(false);
  }

  private mutateRsvp(cancel: boolean): void {
    if (!this.page) return;
    const eventId = this.page.eventId;
    const version = this.loadVersion;
    this.isRsvpWorking = true;
    this.rsvpStatusMessage = '';
    const params = new HttpParams().set('eventId', String(eventId));
    const request = cancel
      ? this.profileService.virtualEventCancelRsvp(params)
      : this.profileService.virtualEventRsvp(params);
    request.pipe(take(1)).subscribe({
      next: (response: any) => {
        if (!this.isCurrent(eventId, version)) return;
        this.isRsvpWorking = false;
        if (response?.success === 0 && response.data) {
          this.applyViewerState(response.data);
          this.viewerStateError = false;
          this.rsvpIntentService.clear();
          this.rsvpStatusMessage = cancel
            ? 'Your RSVP has been cancelled. Calendar entries you added are not removed automatically.'
            : 'Your RSVP is confirmed.';
        } else {
          this.invalidateViewerAccess();
          const reason = response?.errorCode || response?.message;
          if (isTerminalRsvpReason(reason)) this.rsvpIntentService.clear();
          this.rsvpStatusMessage = rsvpReasonMessage(reason);
        }
        this.reconcileLandingPage();
      },
      error: (error: any) => {
        if (!this.isCurrent(eventId, version)) return;
        this.isRsvpWorking = false;
        this.invalidateViewerAccess();
        const reason =
          error?.error?.errorCode ||
          error?.error?.message ||
          error?.errorCode ||
          error?.message;
        if (isTerminalRsvpReason(reason)) this.rsvpIntentService.clear();
        this.rsvpStatusMessage = isTerminalRsvpReason(reason)
          ? rsvpReasonMessage(reason)
          : 'Your RSVP update could not be confirmed. Check event status before trying again.';
        this.reconcileLandingPage();
      },
    });
  }

  private watchEvent(): void {
    if (!this.page) return;
    // EventComponent owns the authorized Watch handoff and embeds the provider.
    // Keeping this navigation first-party preserves the CitizeX event shell.
    void this.router.navigate(['/profile/events/', this.page.eventId], {
      state: {
        eventLandingWatchMode: this.viewerState.watchMode,
        eventLandingEventId: this.page.eventId,
      },
    });
  }

  private updateCountdown(): void {
    if (!this.page) {
      this.countdown = null;
      return;
    }

    const now = Date.now();
    const startsAt = new Date(this.page.startsAt).getTime();
    this.hasScheduledStartArrived =
      Number.isFinite(startsAt) && now >= startsAt;
    if (this.page.status !== 'UPCOMING') {
      this.countdown = null;
      this.stopCountdown();
      return;
    }

    if (now >= startsAt) {
      this.countdown = null;
      this.stopCountdown();
      return;
    }

    const remaining = startsAt - now;
    if (!Number.isFinite(remaining) || remaining <= 0) {
      this.countdown = null;
      this.stopCountdown();
      return;
    }

    this.countdown = {
      days: Math.floor(remaining / 86_400_000),
      hours: Math.floor((remaining % 86_400_000) / 3_600_000),
      minutes: Math.floor((remaining % 3_600_000) / 60_000),
      seconds: Math.floor((remaining % 60_000) / 1000),
    };
  }

  private showUnavailable(): void {
    this.stopCountdown();
    this.page = null;
    this.countdown = null;
    this.isLoading = false;
    this.isUnavailable = true;
    this.loadError = false;
    this.isRefreshing = false;
    this.isViewerStateLoading = false;
    this.restoreDefaultMetadata();
  }

  private stopCountdown(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = undefined;
    }
  }

  private refreshEventState(afterRefresh?: () => void): void {
    if (!this.page || this.isBusy) return;
    const eventId = this.page.eventId;
    const version = this.loadVersion;
    const signedIn = this.authService.getLogflag();
    this.isRefreshing = true;
    this.rsvpStatusMessage = '';
    const publicPage = this.read(
      this.profileService.fetchPublicEventLandingPage(eventId)
    ).pipe(catchError(() => of(null)));
    const viewer = signedIn
      ? this.read(
          this.profileService.fetchVirtualEventWatchState(eventId)
        ).pipe(catchError(() => of(null)))
      : of(null);
    forkJoin({ publicPage, viewer }).subscribe(
      ({ publicPage: response, viewer: state }: any) => {
        if (!this.isCurrent(eventId, version)) return;
        this.isRefreshing = false;
        if (!this.applyPublicResponse(response, eventId, version)) return;
        if (signedIn) {
          if (state?.success !== 0 || !state.data) {
            this.markViewerStateUnavailable();
            return;
          }
          this.applyViewerState(state.data);
          this.viewerStateError = false;
        }
        this.checkedWaiting = this.isWaitingForHost;
        if (this.page?.status === 'ENDED' || this.page?.status === 'CANCELLED')
          return;
        afterRefresh?.();
      }
    );
  }

  private applyPublicResponse(
    response: any,
    eventId: number,
    version: number
  ): boolean {
    if (response?.success !== 0) {
      this.rsvpStatusMessage =
        'Unable to refresh this event. Please try again.';
      return false;
    }
    if (!response.data) {
      this.showUnavailable();
      this.redirectToEventDetails(eventId, version);
      return false;
    }
    const page = response.data as EventLandingPage;
    if (page.eventId !== eventId || !page.slug || !page.hostTag) {
      this.rsvpStatusMessage =
        'Unable to refresh this event. Please try again.';
      return false;
    }
    this.setPage(page);
    return true;
  }

  private reconcileLandingPage(): void {
    if (!this.page) return;
    const eventId = this.page.eventId;
    const version = this.loadVersion;
    const originalMessage = this.rsvpStatusMessage;
    this.isRefreshing = true;
    this.read(this.profileService.fetchPublicEventLandingPage(eventId))
      .pipe(catchError(() => of(null)))
      .subscribe((response: any) => {
        if (!this.isCurrent(eventId, version)) return;
        this.isRefreshing = false;
        const applied = this.applyPublicResponse(response, eventId, version);
        if (applied && this.isHost && this.page?.status === 'LIVE') {
          this.viewerStateError = false;
          this.rsvpStatusMessage = '';
          return;
        }
        if (
          applied &&
          !this.isHost &&
          this.authService.getLogflag() &&
          ((this.page?.status === 'LIVE' &&
            this.viewerState.watchReason === 'EVENT_NOT_LIVE') ||
            (this.page?.status === 'UPCOMING' && this.viewerState.canWatch))
        ) {
          this.invalidateViewerAccess();
        }
        this.rsvpStatusMessage = [
          originalMessage,
          !applied
            ? 'Unable to refresh event details. Check event status to try again.'
            : '',
        ]
          .filter(Boolean)
          .join(' ');
      });
  }

  private showLoadError(): void {
    this.stopCountdown();
    this.page = null;
    this.countdown = null;
    this.isLoading = false;
    this.isViewerStateLoading = false;
    this.isUnavailable = false;
    this.loadError = true;
    this.restoreDefaultMetadata();
  }

  private applyLandingMetadata(page: EventLandingPage): void {
    const canonicalUrl = `${this.document.location.origin}/events/${
      page.eventId
    }/${encodeURIComponent(page.slug)}`;
    const lifecycleLabel =
      page.status === 'ENDED'
        ? 'Event ended'
        : page.status === 'CANCELLED'
        ? 'Event cancelled'
        : '';
    const title = `${page.heading}${
      lifecycleLabel ? ` — ${lifecycleLabel}` : ''
    } | CitizeX`;
    const description = this.truncateMetadataDescription(
      [
        lifecycleLabel ? `${lifecycleLabel}.` : '',
        page.description || 'View this event on CitizeX.',
      ]
        .filter(Boolean)
        .join(' ')
    );
    const image =
      page.imageUrl ||
      `${this.document.location.origin}/assets/images/logo-text.png`;
    const imageAlt = page.imageUrl ? page.imageAlt || page.heading : 'CitizeX';

    this.title.setTitle(title);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({
      property: 'og:title',
      content: title,
    });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:url', content: canonicalUrl });
    this.meta.updateTag({ property: 'og:image', content: image });
    this.meta.updateTag({ property: 'og:image:alt', content: imageAlt });
    this.meta.updateTag({
      name: 'twitter:card',
      content: page.imageUrl ? 'summary_large_image' : 'summary',
    });
    this.meta.updateTag({
      name: 'twitter:title',
      content: title,
    });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: image });
    this.meta.updateTag({ name: 'twitter:image:alt', content: imageAlt });

    this.setCanonicalUrl(canonicalUrl);
  }

  private truncateMetadataDescription(value: string): string {
    const normalized = value.replace(/\s+/g, ' ').trim();
    return normalized.length > 240
      ? `${normalized.slice(0, 239).trimEnd()}…`
      : normalized;
  }

  private restoreDefaultMetadata(nextPath?: string): void {
    const currentPath =
      nextPath || this.router.url || this.document.location.pathname || '/';
    const canonicalUrl = `${this.document.location.origin}${currentPath}`;
    const description = 'Join the conversation on CitizeX.';
    const image = `${this.document.location.origin}/assets/images/logo-text.png`;

    this.title.setTitle('CitizeX');
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:title', content: 'CitizeX' });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:url', content: canonicalUrl });
    this.meta.updateTag({ property: 'og:image', content: image });
    this.meta.updateTag({ property: 'og:image:alt', content: 'CitizeX' });
    this.meta.updateTag({ name: 'twitter:card', content: 'summary' });
    this.meta.updateTag({ name: 'twitter:title', content: 'CitizeX' });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: image });
    this.meta.updateTag({ name: 'twitter:image:alt', content: 'CitizeX' });
    this.setCanonicalUrl(canonicalUrl);
  }

  private setCanonicalUrl(canonicalUrl: string): void {
    this.canonicalLink =
      this.document.querySelector('link[rel="canonical"]') || undefined;
    if (!this.canonicalLink) {
      this.canonicalLink = this.document.createElement('link');
      this.canonicalLink.rel = 'canonical';
      this.document.head.appendChild(this.canonicalLink);
    }
    this.canonicalLink.href = canonicalUrl;
  }
}
